import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUserId, getUserRole, getUserEmail } from "../../utils/tokenUtils";
import UserProfileBox from "./UserProfileBox";
import "./LogoutModal.css";
import "./UserProfileBox.css";
import ProfileIcon from "./icons/ProfileIcon";
import TeamIcon from "./icons/TeamIcon";
import TutorialIcon from "./icons/TutorialIcon";
import AppsIcon from "./icons/AppsIcon";
import LogoutIcon from "./icons/LogoutIcon";

const toTitleCase = (str: string) =>
  str
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

interface ProfilePopupProps {
  onClose: () => void;
}

const ProfilePopup: React.FC<ProfilePopupProps> = ({ onClose }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [role, setRole] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [email, setEmail] = useState("");
  const [userId, setUserId] = useState(getUserId());
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setFirstName(localStorage.getItem("firstName") || "");
    setLastName(localStorage.getItem("lastName") || "");
    setProfileImage(localStorage.getItem("profileImage") || "");
    setRole(getUserRole());
    setPhone(localStorage.getItem("phone") || "");
    setDob(localStorage.getItem("dob") || "");
    setEmail(localStorage.getItem("Login Email") || "");
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [onClose]);

  return (
    <div className="mp-overlay" onClick={onClose}>
      <div className="mp-modal" ref={popupRef} onClick={(e) => e.stopPropagation()}>
        <button className="mp-close-btn" onClick={onClose} aria-label="Close">×</button>

        <div className="mp-header-band" />

        <div className="mp-body">
          <div className="mp-avatar">
            {profileImage
              ? <img src={profileImage} alt="Profile" className="mp-avatar-img" />
              : <>{firstName.charAt(0).toUpperCase()}{lastName.charAt(0).toUpperCase()}</>
            }
          </div>

          <h2 className="mp-name">{toTitleCase(firstName)} {toTitleCase(lastName)}</h2>
          <span className="mp-role-badge">
            {role === "Admin" ? "AI Workforce Sponsor" : "Content Workflow Manager"}
          </span>

          <div className="mp-divider" />

          <div className="mp-info-list">
            <div className="mp-info-row">
              <span className="mp-info-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                </svg>
              </span>
              <div className="mp-info-text">
                <span className="mp-info-label">Email</span>
                <span className="mp-info-value">{email || "N/A"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
interface CompanyProfilePopupProps {
  onClose: () => void;
}

const CompanyProfilePopup: React.FC<CompanyProfilePopupProps> = ({
  onClose,
}) => {
  const [companyName, setCompanyName] = useState("");
  const [companyLogo, setCompanyLogo] = useState("");
  const [companyType, setCompanyType] = useState("");
  const [location, setLocation] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [companyUrl, setCompanyUrl] = useState("");
  const [about, setAbout] = useState("");
  const [userId, setUserId] = useState(getUserId() || "6");

  useEffect(() => {
    // Replace with actual logic or API data
    setCompanyName("Joe’s Gear and Apparel");
    setCompanyLogo("/images/companylogo.png");
    setCompanyType("Retail company");
    setLocation("Pennsylvania, United States");
    setEmail("mail.joesgear@gmail.com");
    setPhone("+91 - 8978645789");
    setCompanyUrl("www.joesgearandapparel.com");
    setAbout(
      "Joe’s Gear and Apparel, founded in 1948, is a nationwide sporting goods retailer with a mission to help athletes achieve their goals and reach their full potential, offering a wide variety of sports equipment, apparel, and footwear, both online and in-store."
    );
  }, []);

  return (
    <div className="popup-overlay">
      <div className="profile-popup">
        <div className="popup-header">
          <h3>Company Profile</h3>
          <img
            src="/images/cross.svg"
            alt="Close"
            className="task-icon"
            onClick={onClose}
          />
        </div>
        <hr className="divider" />

        <div className="popup-body company-popup">
          <div className="company-card">
            <div className="company-info">
              <img
                src="/images/companyimage.svg"
                alt="Company Logo"
                className="company-logo"
              />
              <h4>{companyName}</h4>
              <p className="company-type">{companyType}</p>
              <p className="company-location">{location}</p>
            </div>
          </div>

          <hr className="divider-two" />

          <div className="company-popup-wrapper">
            {/* Row layout for email & phone */}
            <div className="company-row-fields">
              <div className="company-item">
                <div className="company-icon-text">
                  <img src="/images/email.svg" alt="Email Icon" />
                  <div className="company-text-block">
                    <span className="company-label">Email:</span>
                    <span>{email}</span>
                  </div>
                </div>
              </div>

              <div className="company-item">
                <div className="company-icon-text">
                  <img src="/images/phone.svg" alt="Phone Icon" />
                  <div className="company-text-block">
                    <span className="company-label">Phone:</span>
                    <span>{phone}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Column layout for URL & About */}
            <div className="company-item">
              <div className="company-icon-text">
                <img src="/images/companyurl.svg" alt="Company URL Icon" />
                <div className="company-text-block">
                  <span className="company-label">Company URL:</span>
                  <span>{companyUrl}</span>
                </div>
              </div>
            </div>

            <div className="company-item">
              <div className="company-icon-text">
                <img src="/images/aboutcompany.svg" alt="About Icon" />
                <div className="company-text-block">
                  <span className="company-label">About Company:</span>
                  <p>{about}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
interface LogoutModalProps {
  onClose: () => void;
  onLogout: () => void;
}

const LogoutModal: React.FC<LogoutModalProps> = ({ onClose, onLogout }) => {
  return (
    <div className="logout-overlay" onClick={onClose}>
      <div className="logout-modal" onClick={(e) => e.stopPropagation()}>
        <button className="logout-close-btn" onClick={onClose} aria-label="Close">×</button>
        <div className="logout-body">
          <div className="logout-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </div>
          <p className="logout-title">Log Out</p>
          <p className="logout-subtitle">Are you sure you want to log out?</p>
          <div className="logout-actions">
            <button className="cancel-logout-btn" onClick={onClose}>Cancel</button>
            <button className="logout-btn" onClick={onLogout}>Log Out</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const UserDropdown: React.FC = () => {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [role, setRole] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const [showCompanyProfilePopup, setShowCompanyProfilePopup] = useState(false);
  const [showLogout, setShowLogout] = useState(false);
  const navigate = useNavigate();
  const handleLogout = () => {
    // Save the mobile warning flag before clearing localStorage
    const mobileWarningDismissed = localStorage.getItem("mobileDesktopWarningDismissed");
    
    // Perform logout logic
    localStorage.clear(); // Clear all data
    
    // Restore the mobile warning flag so it won't show again
    if (mobileWarningDismissed) {
      localStorage.setItem("mobileDesktopWarningDismissed", mobileWarningDismissed);
    }
    
    navigate("/"); // or the main screen route
    window.location.reload();
  };
  useEffect(() => {
    setFirstName(localStorage.getItem("firstName") || "");
    setLastName(localStorage.getItem("lastName") || "");
    setProfileImage(localStorage.getItem("profileImage") || "");
    setRole(getUserRole());
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  const menuItems: { label: string; Icon: React.FC<{ className?: string }>; action: () => void }[] = [
    {
      label: "My Profile",
      Icon: ProfileIcon,
      action: () => setShowProfilePopup(true),
    },
    {
      label: "My Team",
      Icon: TeamIcon,
      action: () => navigate("/team"),
    },
    {
      label: "Tutorials",
      Icon: TutorialIcon,
      action: () => navigate("/tutorial"),
    },
    ...(role === "Admin"
      ? [
          {
            label: "Apps",
            Icon: AppsIcon,
            action: () => navigate("/apps"),
          },
        ]
      : []),
    {
      label: "Log out",
      Icon: LogoutIcon,
      action: () => setShowLogout(true),
    },
  ];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }

    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, []);

  return (
    <div className="user-profile" ref={dropdownRef}>
      {/* <img
        src="/images/Notifications.svg"
        alt="Notifications"
        className="notification-icon"
      /> */}

      <div className="profile-container" onClick={toggleDropdown}>
        <div className="profile-avatar">
          {profileImage ? (
            <img src={profileImage} alt="User" className="profile-pic" />
          ) : (
            <span>
              {firstName || lastName
                ? `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`
                : "NP"}
            </span>
          )}
        </div>
        <div className="profile-info">
          <span className="profile-name">
            {toTitleCase(firstName)} {toTitleCase(lastName)}
          </span>
          <span className="profile-role">
            {role === "Admin"
              ? "AI Workforce Sponsor"
              : "Content Workflow Manager"}
          </span>
        </div>
        <svg className="profile-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points={isDropdownOpen ? "6 15 12 9 18 15" : "6 9 12 15 18 9"} />
        </svg>
      </div>

      <div
        className={`dropdown-menu ${isDropdownOpen ? "is-open" : ""}`}
        aria-hidden={!isDropdownOpen}
      >
        {menuItems.map(({ label, Icon, action }, index) => (
          <div
            className="dropdown-item"
            key={index}
            style={{ transitionDelay: isDropdownOpen ? `${index * 30}ms` : "0ms" }}
            onClick={() => {
              if (!isDropdownOpen) return;
              action?.();
              setIsDropdownOpen(false);
            }}
          >
            <Icon />
            <span>{label}</span>
          </div>
        ))}
      </div>

      {showProfilePopup && (
        <ProfilePopup onClose={() => setShowProfilePopup(false)} />
      )}

      {showCompanyProfilePopup && (
        <CompanyProfilePopup
          onClose={() => setShowCompanyProfilePopup(false)}
        />
      )}
      {showLogout && (
        <LogoutModal
          onClose={() => setShowLogout(false)}
          onLogout={handleLogout}
        />
      )}
    </div>
  );
};

export default UserDropdown;
